import { useCallback, useMemo } from "react";
import { toast } from "sonner";

import {
  getCallConnection,
  invokeCallHub,
  registerHandler,
  startCallConnection,
} from "@/realtime/callHubClient.js";
import { CALL_STATES } from "@/calling/callConstants.js";
import { useCallEngine } from "@/calling/hooks/useCallEngine.js";
import { CALL_HUB_EVENTS, CALL_HUB_METHODS } from "@/calling/realtime/callEvents.js";
import {
  departmentDisplayName,
  HOTEL_DEPARTMENTS,
  normalizeDepartment,
} from "@/utils/alerts/departmentAlerts.js";

const callIdFrom = (payload = {}) => payload.callId || payload.CallId || "";

const createGuestTarget = (department = HOTEL_DEPARTMENTS.reception) => {
  const targetDepartment = normalizeDepartment(department, HOTEL_DEPARTMENTS.reception);
  const targetDepartmentLabel = departmentDisplayName(targetDepartment);

  return {
    targetId: targetDepartment,
    targetType: "department",
    targetLabel: `Calling ${targetDepartmentLabel}`,
    subtitle: `Waiting for ${targetDepartmentLabel} to answer...`,
    metadata: {
      targetDepartment,
      targetDepartmentLabel,
    },
  };
};

const createGuestIncomingDisplay = (payload = {}) => {
  const targetDepartment = normalizeDepartment(payload.targetDepartment || payload.TargetDepartment || HOTEL_DEPARTMENTS.reception);
  const targetDepartmentLabel = departmentDisplayName(targetDepartment);
  const callerName =
    payload.staffName ||
    payload.StaffName ||
    payload.callerName ||
    payload.CallerName ||
    targetDepartmentLabel;

  return {
    ...payload,
    callId: callIdFrom(payload),
    title: `${callerName} is calling`,
    subtitle: `Incoming ${targetDepartmentLabel} call`,
    targetDepartment,
  };
};

const createGuestHubClient = ({ connection, guestSessionId, guestInfo }) => {
  const registerGuest = () => invokeCallHub(connection, "RegisterGuest", guestSessionId);

  return {
    connection,
    startConnection: () => startCallConnection({ mode: "guest", guestSessionId }),
    invoke: (methodName, payload = {}) => {
      if (
        methodName === CALL_HUB_METHODS.registerParticipant ||
        methodName === CALL_HUB_METHODS.registerReceiver
      ) {
        return registerGuest();
      }

      if (methodName === CALL_HUB_METHODS.startCall) {
        const targetDepartment = payload.target?.metadata?.targetDepartment || payload.target?.targetId;
        return invokeCallHub(connection, "StartGuestCall", {
          guestSessionId,
          guestName: guestInfo?.guestName || "Guest",
          roomName: guestInfo?.roomName || "",
          targetDepartment: normalizeDepartment(targetDepartment, HOTEL_DEPARTMENTS.reception),
        });
      }

      return invokeCallHub(connection, methodName, payload);
    },
    registerHandler: (eventName, handler) => {
      const eventAliases = {
        [CALL_HUB_EVENTS.participantRegistered]: ["GuestRegistered", "GuestPresenceChanged", "guestpresencechanged"],
        [CALL_HUB_EVENTS.receiverRegistered]: ["GuestRegistered", "GuestPresenceChanged", "guestpresencechanged"],
        [CALL_HUB_EVENTS.incomingCall]: ["IncomingCall", "IncomingReceptionCall", "incomingcall"],
      }[eventName] || [eventName];

      const cleanups = eventAliases.map((alias) => registerHandler(connection, alias, handler));

      // Register presence handlers to silence SignalR unhandled event warnings
      const snapshotCleanup = registerHandler(connection, "GuestPresenceSnapshot", () => {});
      const changedCleanup = registerHandler(connection, "GuestPresenceChanged", () => {});

      return () => {
        cleanups.forEach((cleanup) => cleanup());
        snapshotCleanup();
        changedCleanup();
      };
    },
  };
};

export const useGuestCall = ({ guestInfo, remoteAudioRef }) => {
  const guestSessionId = guestInfo?.sessionId || "";
  const guestName = guestInfo?.guestName || "Guest";
  const roomName = guestInfo?.roomName || "";
  const organizationId = guestInfo?.organizationId || guestInfo?.tenantId || "";

  const connection = useMemo(
    () => (guestSessionId ? getCallConnection({ mode: "guest", guestSessionId }) : null),
    [guestSessionId],
  );
  const identity = useMemo(() => {
    if (!guestSessionId) return null;
    return {
      participantId: guestSessionId,
      participantType: "guest-session",
      displayName: guestName,
      tenantId: organizationId,
      metadata: {
        roomName,
      },
    };
  }, [guestName, organizationId, roomName, guestSessionId]);

  const receiveScopes = useMemo(
    () => (guestSessionId ? [`guest-session:${guestSessionId}`] : []),
    [guestSessionId],
  );

  const hubClient = useMemo(
    () => (connection && guestSessionId ? createGuestHubClient({ connection, guestSessionId, guestInfo: { guestName, roomName, sessionId: guestSessionId } }) : null),
    [connection, guestSessionId, guestName, roomName],
  );

  const engine = useCallEngine({
    hubClient,
    identity,
    receiveScopes,
    remoteAudioRef,
    mode: "receiver",
    mapIncomingCall: createGuestIncomingDisplay,
    onError: (message) => toast.error(message),
  });

  const status =
    engine.registrationState === "registered"
      ? "connected"
      : engine.registrationState || "idle";
  const registered = engine.registrationState === "registered";

  const callDepartment = useCallback(async (department = HOTEL_DEPARTMENTS.reception) => {
    const target = createGuestTarget(department);
    await engine.startCall(target);
  }, [engine]);

  const callReception = useCallback(
    () => callDepartment(HOTEL_DEPARTMENTS.reception),
    [callDepartment],
  );

  return {
    callState: engine.callState,
    callInfo: engine.callInfo,
    muted: engine.isMuted,
    isHeld: engine.isHeld,
    isRemoteHeld: engine.isRemoteHeld,
    isTransferring: engine.isTransferring,
    transferringDepartment: engine.transferringDepartment,
    callDuration: engine.callDuration,
    status,
    registered,
    setCallInfo: () => {},
    setCallState: () => {},
    resetCall: engine.cleanup,
    loadIceServers: () => Promise.resolve(),
    acceptCall: engine.acceptCall,
    declineCall: engine.declineCall,
    endCall: engine.endCall,
    createOffer: () => Promise.resolve(),
    handleSignal: () => {},
    handleCallEnded: engine.cleanup,
    toggleMute: engine.toggleMute,
    callDepartment,
    callReception,
  };
};
